import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/Shared";
import UploadDropzone from "../components/UploadDropzone";
import InstallHint from "../components/InstallHint";
import CameraPermissionTip from "../components/CameraPermissionTip";
import DocumentScanner from "../components/scanner/Scanner";
import { autoCropPhoto } from "../components/scanner/autoCrop";
import { optimisePhotoFile, optimisePhotoFileDebug } from "../components/scanner/photoPipeline";

// Preview-only hook so the real browser pipeline can be inspected from automation (never in production builds)
if (process.env.NODE_ENV === "development") window.__supplivoPhotoPipeline = { optimisePhotoFileDebug };

const newUploadId = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
const retryable = (e) => !e.response || e.response.status >= 500 || e.code === "ECONNABORTED";

// Flag on: up to 2 automatic retries; the same client_upload_id makes a retried request idempotent on the server (no duplicate invoices).
async function postUpload(fd, withRetry) {
  if (!withRetry) return api.post("/invoices/upload", fd, { timeout: 120000 });
  fd.append("client_upload_id", newUploadId());
  for (let attempt = 0; ; attempt++) {
    try { return await api.post("/invoices/upload", fd, { timeout: 120000 }); } catch (e) {
      if (attempt >= 2 || !retryable(e)) throw e;
      await new Promise((r) => setTimeout(r, 1500 * (attempt + 1)));
    }
  }
}

export default function Scan() {
  const { restaurantId, user, features } = useAuth();
  const navigate = useNavigate();
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [busyLabel, setBusyLabel] = useState("");
  useEffect(() => {
    if (!busy) return;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);
  const autoDefault = (localStorage.getItem("supplivo_auto_capture") ?? String(user?.preferences?.auto_capture ?? true)) === "true";
  // Feature flag from the backend (IMG_OPTIMISE_ENABLED): one cropped + resized image per photo instead of original + crop
  const optimise = !!features?.img_optimise;
  const optimiseOpts = { maxSide: features?.photo_max_side || 2000, quality: features?.photo_jpeg_quality || 0.85 };

  const BATCH = 3; // small requests keep each call well under proxy time limits when uploading many photos

  const upload = async (files, groupPages) => {
    if (!restaurantId) return toast.error("Select a restaurant first");
    setBusy(true);
    let prepared = null; // optimised photos (flag on): processed one at a time so only one large image is in memory
    if (optimise) {
      prepared = [];
      for (const [i, f] of files.entries()) {
        setBusyLabel(`Preparing photo ${i + 1} of ${files.length}…`);
        let r = null;
        try { r = await optimisePhotoFile(f, optimiseOpts); } catch { r = null; }
        prepared.push(r ? { file: new File([r.blob], f.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" }), meta: r.meta } : { file: f, meta: null });
      }
      setBusyLabel("Uploading…");
    }
    const send = async (batch, offset) => {
      const fd = new FormData();
      if (optimise) {
        const items = prepared.slice(offset, offset + batch.length);
        items.forEach((p) => fd.append("files", p.file, p.file.name));
        fd.append("client_meta", JSON.stringify(items.map((p) => p.meta)));
      } else {
        batch.forEach((f) => fd.append("files", f));
        // photos of paper invoices are cropped to the document client-side (same as the camera scanner); PDFs pass through
        const crops = await Promise.all(batch.map((f) => autoCropPhoto(f)));
        crops.forEach((blob, i) => { if (blob) fd.append("processed", blob, `p${i}__${batch[i].name.replace(/\.[^.]+$/, "")}.jpg`); });
      }
      fd.append("restaurant_id", restaurantId);
      fd.append("source", "upload");
      fd.append("group_pages", groupPages ? "true" : "false");
      const { data } = await postUpload(fd, optimise);
      return data.count;
    };
    try {
      if (groupPages || files.length <= BATCH) {
        const count = await send(files, 0);
        toast.success(`${count} invoice${count > 1 ? "s" : ""} queued for AI reading`);
      } else {
        let done = 0, failed = [];
        const id = toast.loading(`Uploading 0 of ${files.length}…`);
        for (let i = 0; i < files.length; i += BATCH) {
          const batch = files.slice(i, i + BATCH);
          try { await send(batch, i); done += batch.length; } catch (e) { failed.push(...batch.map((f) => f.name)); }
          toast.loading(`Uploading ${Math.min(i + BATCH, files.length)} of ${files.length}…`, { id });
        }
        toast.dismiss(id);
        if (failed.length) toast.error(`${failed.length} file${failed.length > 1 ? "s" : ""} failed to upload — please retry: ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}`, { duration: 10000 });
        if (done) toast.success(`${done} invoice${done > 1 ? "s" : ""} queued for AI reading`);
        if (!done) throw new Error("upload failed");
      }
      navigate("/invoices");
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    } finally {
      setBusy(false);
      setBusyLabel("");
    }
  };

  const finishScan = async (invoices, onProgress) => {
    let ok = 0, failed = 0;
    for (const [idx, inv] of invoices.entries()) {
      onProgress?.({ done: idx, total: invoices.length });
      const fd = new FormData();
      if (optimise) {
        inv.pages.forEach((p, i) => fd.append("files", p.photo, `scan-${i + 1}.jpg`));
        fd.append("client_meta", JSON.stringify(inv.pages.map((p) => p.meta)));
      } else {
        inv.pages.forEach((p, i) => fd.append("files", p.original, `scan-${i + 1}.jpg`));
        inv.pages.forEach((p, i) => fd.append("processed", p.processed, `scan-${i + 1}-processed.jpg`));
      }
      fd.append("restaurant_id", restaurantId);
      fd.append("source", "scan");
      fd.append("group_pages", "true");
      try { await postUpload(fd, optimise); ok++; } catch (e) { failed++; toast.error(errorMessage(e)); }
    }
    onProgress?.({ done: invoices.length, total: invoices.length });
    if (ok) toast.success(`${ok} scanned invoice${ok > 1 ? "s" : ""} sent for AI reading${failed ? ` · ${failed} failed to upload` : ""}`);
    setScanning(false);
    navigate("/invoices");
  };

  const onAutoChange = (v) => {
    localStorage.setItem("supplivo_auto_capture", String(v));
    api.put("/settings/preferences", { auto_capture: v }).catch(() => {});
  };

  return (
    <div data-testid="scan-page">
      <PageHeader title="Scan Invoice" subtitle="Photograph paper invoices with the camera, or upload PDFs and photos." />
      <div className="mb-4 space-y-3"><InstallHint compact /><CameraPermissionTip /></div>
      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 fade-up">
          <button onClick={() => setScanning(true)} data-testid="open-scanner-button" className="w-full rounded-xl bg-slate-950 text-white p-6 sm:p-8 text-left relative overflow-hidden group transition-transform active:scale-[0.99]">
            <div className="absolute -right-8 -top-8 h-40 w-40 rounded-full bg-emerald-500/20 group-hover:bg-emerald-500/30 transition-colors pointer-events-none" style={{ contain: "paint" }} />
            <span className="h-14 w-14 rounded-full bg-emerald-500 grid place-items-center text-slate-950 mb-5"><Camera className="h-7 w-7" /></span>
            <p className="font-display text-2xl font-bold">Scan with camera</p>
            <p className="text-slate-400 text-sm mt-1">Auto Capture detects each invoice, holds steady and shoots — keep feeding paper until you're done.</p>
            <span className="inline-block mt-5 text-sm font-semibold text-emerald-300">Open scanner →</span>
          </button>
          <ul className="mt-4 text-sm text-slate-600 space-y-1.5 px-1">
            <li>• <b>SINGLE</b>: one invoice — captures and sends straight away.</li>
            <li>• <b>MULTIPLE</b>: keep feeding invoices; each capture is a separate invoice.</li>
            <li>• <b>COMBINE</b>: a 2–3 page invoice — all captures are merged into one invoice.</li>
            <li>• Place the invoice on a dark, plain surface for best edge detection.</li>
          </ul>
        </div>
        <div className="lg:col-span-3 fade-up">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-2"><Upload className="h-4 w-4" /> Upload files</div>
          {busy && (
            <div className="mb-3 rounded-xl bg-rose-600 text-white px-4 py-3 flex items-center gap-3 shadow-lg" role="alert" data-testid="upload-guard-banner">
              <AlertTriangle className="h-6 w-6 shrink-0" />
              <div><p className="font-display font-bold">Don't close the app — uploading your photos</p><p className="text-xs text-rose-100">Large batches take a while. Leaving now would lose the files still in the browser.</p></div>
            </div>
          )}
          <UploadDropzone onUpload={upload} busy={busy} busyLabel={busyLabel} />
        </div>
      </div>
      {scanning && <DocumentScanner initialAutoCapture={autoDefault} onAutoCaptureChange={onAutoChange} onFinish={finishScan} onClose={() => setScanning(false)} optimise={optimise} optimiseOpts={optimiseOpts} />}
    </div>
  );
}
